import React, { useContext, useState } from "react";
import styles from "./styles.module.scss";
import ResourceRow from "./ResourceRow";
import LockIcon from "../../../../public/images/lock.svg";
import { useDispatch } from "react-redux";
import { deselectEvent, togglePaymentStatus } from "app/actions/events";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { EventType, RepeatType, isResourceBlock } from "app/models/EventModel";
import { UserContext, RoleType } from "app/contexts/UserContext";
import { canManageResourceBlocks } from "app/routes/Calendar/EventForm/blockOwner";
import { useHistory } from "react-router-dom";
import { cls } from "app/utils";
import { formatFullDate, formatTime } from "app/utils/luxonFormats";
import useCalendarPreferences from "app/utils/hooks/useCalendarPreferences";
import useCopyToClipboard from "app/utils/hooks/useCopyToClipboard";
import UserAvatar from "app/components/UserAvatar";
import ScheduleModal from "app/components/SpaceTabsContainer/ScheduleModal";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import { StudentProfileModel } from "app/models/scheduling/StudentProfileModel";
import {
  CardIcon,
  CashIcon,
  LinkIcon,
  EditIcon,
  DuplicateIcon,
  DeleteIcon,
  CloseIcon,
  PaymentMethodIcon,
} from "../PaymentIcons";

interface ScheduleContact {
  athleteId: string;
  externalId: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  profilePicture?: string;
  avatarUrl?: string;
}

const ScheduleBlocksInfo = ({ event, onEdit, onDelete, onCopy }) => {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const { preferences } = useCalendarPreferences();

  const { copyToClipboard } = useCopyToClipboard();

  // CN-2558: clicking a contact opens the same "bookings" schedule modal used
  // by the "View" button in Contacts (SingleContact).
  const [scheduleContact, setScheduleContact] =
    useState<ScheduleContact | null>(null);
  const [isUSGAIntegrationActive, setIsUSGAIntegrationActive] =
    useState<boolean>(false);

  const openContactSchedule = async (contact: ScheduleContact) => {
    setIsUSGAIntegrationActive(false);
    setScheduleContact(contact);
    if (!contact.externalId) {
      return;
    }
    try {
      const integrations = await schedulingFetchGet(
        `/student_profiles/${contact.externalId}/integrations`,
      );
      const ghin = (integrations || []).find(
        (integration: { name: string }) => integration.name === "ghin",
      );
      setIsUSGAIntegrationActive(ghin?.status === "active");
    } catch {
      // Integrations are optional; fall back to the bookings-only modal.
    }
  };

  const toContact = (studentProfile: StudentProfileModel): ScheduleContact => ({
    athleteId: studentProfile.id,
    externalId: studentProfile.id,
    fullName: studentProfile.fullName,
    firstName: studentProfile.firstName,
    lastName: studentProfile.lastName,
    email: studentProfile.email,
    profilePicture: studentProfile.profilePicture,
    avatarUrl: studentProfile.profilePicture,
  });

  // CN-2558 + CN-1323: when someone other than the participant pays (a family
  // head paying for a dependent), surface a distinct "Payer" row. The payer is
  // the participant's head; `paying_athlete_id` is that head's external id.
  const participants: StudentProfileModel[] = event.studentProfiles || [];
  const payerExternalId = event.paying_athlete_id;
  const payerIsParticipant = participants.some(
    (sp) => sp.id === payerExternalId,
  );
  const hasDistinctPayer =
    !!payerExternalId && participants.length > 0 && !payerIsParticipant;
  const payerName =
    participants.find((sp) => sp.headExternalId === payerExternalId)
      ?.headFullName || null;

  // CN-2558: recurrence text matching Figma, e.g. "Weekly on Sunday until
  // July 19, 2026" instead of the bare "Weekly".
  const untilDateValid =
    event.untilDate instanceof Date &&
    !Number.isNaN(event.untilDate.getTime()) &&
    event.untilDate.getFullYear() > 1971;
  const recurrenceLabel = (() => {
    if (!event.repeat) {
      return event.repeatLabel;
    }
    const weekday = event.date.toLocaleString({ weekday: "long" });
    const until = untilDateValid
      ? ` ${t("until")} ${formatFullDate(event.untilDate, preferences.dateFormat)}`
      : "";
    switch (event.repeat) {
      case RepeatType.WEEKLY:
        return `${t("Weekly on")} ${weekday}${until}`;
      case RepeatType.BIWEEKLY:
        return `${t("Bi-Weekly on")} ${weekday}${until}`;
      case RepeatType.DAILY:
        return `${t("Daily")}${until}`;
      case RepeatType.MONTHLY:
        return `${t("Monthly")}${until}`;
      default:
        return event.repeatLabel;
    }
  })();

  const isCardOnFile =
    event.payment_method === "online" &&
    event.online_payment_type === "card_on_file";
  const isSendInvoice =
    event.payment_method === "online" &&
    event.online_payment_type === "send_invoice";

  const paymentStatusColorClass =
    event.payment_status === "paid" ||
    event.payment_status === "payment_confirmed"
      ? styles.icon_paid
      : event.payment_status === "failed" ||
          event.payment_status === "past_due"
        ? styles.icon_failed
        : styles.icon_pending;

  // CN-2558 (PO feedback): the Paid/Unpaid badge on the Offline Payment row is
  // itself the "Mark Paid / Mark Unpaid" toggle (the header $ icon was removed).
  const paymentStatusText =
    event.payment_status === "paid"
      ? t("Paid")
      : event.payment_status === "pending"
        ? t("Unpaid")
        : event.payment_status === "failed"
          ? t("Failed")
          : event.payment_status === "past_due"
            ? t("Past Due")
            : null;
  const paymentStatusBadgeClass =
    event.payment_status === "paid"
      ? styles.paid_status
      : event.payment_status === "pending"
        ? styles.pending_status
        : event.payment_status === "failed" ||
            event.payment_status === "past_due"
          ? styles.failed_status
          : null;
  const isOfflinePaymentToggle = event.payment_method === "offline";

  const renderPaymentIcon = () => {
    if (event.payment_method === "offline" || event.payment_method === "online") {
      return (
        <PaymentMethodIcon
          paymentMethod={event.payment_method}
          onlinePaymentType={event.online_payment_type}
        />
      );
    }
    return <CardIcon />; // package / other — no dedicated icon yet
  };

  const getPaymentSubLabel = () => {
    if (isCardOnFile) {
      return t("Card on file");
    }
    if (isSendInvoice) {
      return t("Invoice");
    }
    return event?.productName;
  };

  const showInvoiceUrlRow =
    event.type === EventType.LESSON_SCHEDULE &&
    event.payment_method === "online" &&
    typeof event.invoiceUrl === "string" &&
    event.invoiceUrl.length > 0;

  // A card-on-file charge is already paid, so copying a "pay now" link is
  // irrelevant — only offer it for the send-invoice flow (CN-2558).
  const showCopyPaymentLink =
    !isCardOnFile &&
    !!event.sharePaymentUrl &&
    event.sharePaymentUrl !== event.invoiceUrl;

  const handleCopyPaymentLink = () => {
    if (event.sharePaymentUrl) {
      copyToClipboard(event.sharePaymentUrl);
      toast.success(t("Payment link copied"));
    }
  };

  const intervalTimeString = `${formatTime(event.interval.start, preferences.timeFormat)} - ${formatTime(event.interval.end, preferences.timeFormat)} ${event.timeZoneShortString}`;

  const ownEvent =
    user.role === RoleType.COACH && event.coachProfile?.id === user.id;
  const isPublicBlock =
    event.type === EventType.COACH_SCHEDULE_BLOCK && !event.private;
  // An admin or a coach-admin. `behavesLikeAdmin` is the same rule
  // (App.tsx), so lessons, coach blocks and bay blocks read one value.
  const isAdmin = user.behavesLikeAdmin === true;

  // A bay block has no coach. An admin, a coach-admin or a scheduler edits,
  // copies or deletes it. A coach sees it and does nothing else.
  const resourceBlock = isResourceBlock(event);

  let allowEdit: boolean;
  if (event.type === EventType.LESSON_SCHEDULE) {
    allowEdit = isAdmin || ownEvent;
  } else if (resourceBlock) {
    allowEdit = canManageResourceBlocks(user);
  } else {
    allowEdit = ownEvent || (isPublicBlock && isAdmin);
  }
  // The rule "no coach, so show delete" stays for the other events. A bay
  // block has no coach too, so it takes the edit rule.
  const allowDelete = resourceBlock
    ? allowEdit
    : allowEdit || !event.coachProfile;

  const history = useHistory();

  const onFeedIconClick = () => {
    if (event.feedPostId) {
      const targetUrl = `/posts/${event.feedPostId}`;
      dispatch(deselectEvent());
      history.push(targetUrl);
    }
  };

  const getPaymentLabel = (paymentType) => {
    switch (paymentType) {
      case "online":
        return t("Online Payment");
      case "offline":
        return t("Offline Payment");
      case "package":
        return t("Package / Subscription");
      default:
        return "";
    }
  };

  const dispatch = useDispatch();
  return (
    <>
      <div className={styles.header}>
        <div className={styles.event_title}>
          <p>{event.lessonRateTitle || event.title}</p>
          {event.scheduleProviderDisplayName && (
            <div
              className={styles.repeat}
            >{`${t("Booked via ")} ${event.scheduleProviderDisplayName}`}</div>
          )}
        </div>
        <div className={styles.header_actions}>
          {!event.externalCalendarId &&
            allowEdit &&
            !event.scheduleProviderDisplayName &&
            !event.feedPostId && (
              <a onClick={onEdit} data-testid="event-edit-button">
                <EditIcon className={styles.header_icon} />
              </a>
            )}
          {!event.externalCalendarId &&
            allowEdit &&
            !event.scheduleProviderDisplayName &&
            !event.feedPostId && (
              <a onClick={onCopy} data-testid="event-copy-button">
                <DuplicateIcon className={styles.header_icon} />
              </a>
            )}
          {!event.externalCalendarId &&
            allowDelete &&
            !event.scheduleProviderDisplayName &&
            !event.feedPostId && (
              <a
                onClick={event.payment_cancellable !== false ? onDelete : undefined}
                data-testid="event-delete-button"
                style={event.payment_cancellable === false ? { opacity: 0.4, pointerEvents: 'none' } : undefined}
              >
                <DeleteIcon className={styles.header_icon} />
              </a>
            )}
          {event.feedPostId && (
            <a onClick={() => onFeedIconClick()}>
              <i className="ico ico-feed-o" />
            </a>
          )}
          <a onClick={() => dispatch(deselectEvent())}>
            <CloseIcon className={styles.header_icon} />
          </a>
        </div>
      </div>
      <div className={styles.event_subtitle_container}>
        <div className={styles.event_subtitle}>
          <i className="ico ico-calendar"></i>
          <div className={styles.datetime_info}>
            <span>{formatFullDate(event.date, preferences.dateFormat)}</span>
            <span>{intervalTimeString}</span>
          </div>
        </div>
        {recurrenceLabel && (
          <div className={styles.repeat_line}>{recurrenceLabel}</div>
        )}
        <ResourceRow scheduleResources={event.scheduleResources} />
        {participants.length > 0 && <div className={styles.divider} />}
        {participants.map((studentProfile: StudentProfileModel) => (
          <div
            key={studentProfile.id}
            className={cls(styles.event_subtitle, styles.contact_row)}
            onClick={() => openContactSchedule(toContact(studentProfile))}
            role="button"
            tabIndex={0}
            data-testid="event-contact-row"
          >
            <UserAvatar
              sizeHeight={48}
              sizeWidth={48}
              srcName={studentProfile.profilePicture}
              userName={studentProfile.fullName}
              className={styles.contact_avatar}
            />
            <div className={styles.contact_info}>
              <p className={styles.contact_name}>{studentProfile.fullName}</p>
              {hasDistinctPayer ? (
                <p className={styles.contact_email}>{t("Athlete")}</p>
              ) : (
                studentProfile.email && (
                  <p className={styles.contact_email}>{studentProfile.email}</p>
                )
              )}
            </div>
          </div>
        ))}
        {hasDistinctPayer && (
          <div
            className={cls(styles.event_subtitle, styles.contact_row)}
            onClick={() =>
              openContactSchedule({
                athleteId: payerExternalId,
                externalId: payerExternalId,
                fullName: payerName || t("Parent / Guardian"),
              })
            }
            role="button"
            tabIndex={0}
            data-testid="event-payer-row"
          >
            <UserAvatar
              sizeHeight={48}
              sizeWidth={48}
              srcName={undefined}
              userName={payerName || t("Parent / Guardian")}
              className={styles.contact_avatar}
            />
            <div className={styles.contact_info}>
              <p className={styles.contact_name}>{payerName || t("Parent / Guardian")}</p>
              {event.payer_email && (
                <p className={styles.contact_email}>{event.payer_email}</p>
              )}
              <p className={styles.contact_email}>{t("Paying User")}</p>
            </div>
          </div>
        )}
        {getPaymentLabel(event?.payment_method) && (
          <div className={styles.divider} />
        )}
        {getPaymentLabel(event?.payment_method) && (
          <div className={styles.event_subtitle}>
            <div className={styles.payment_info_wrapper}>
              <div className={styles.payment_method_label_container}>
                <span
                  className={cls(styles.icon_circle, paymentStatusColorClass)}
                >
                  {renderPaymentIcon()}
                </span>
                <div className={styles.payment_label_wrapper}>
                  <p className={styles.payment_label}>
                    {getPaymentLabel(event?.payment_method)}
                  </p>
                  {getPaymentSubLabel() && (
                    <p className={styles.package_detail}>
                      {getPaymentSubLabel()}
                    </p>
                  )}
                </div>
              </div>
              {paymentStatusText &&
                (isOfflinePaymentToggle ? (
                  <span
                    className={cls(
                      styles.payment_status,
                      paymentStatusBadgeClass,
                      styles.payment_status_toggle,
                    )}
                    onClick={() => dispatch(togglePaymentStatus(event))}
                    role="button"
                    tabIndex={0}
                    data-testid="toggle-payment-status"
                  >
                    {paymentStatusText}
                    <span className={styles.payment_status_divider} />
                    <CashIcon className={styles.payment_status_icon} />
                  </span>
                ) : (
                  <span
                    className={cls(styles.payment_status, paymentStatusBadgeClass)}
                  >
                    {paymentStatusText}
                  </span>
                ))}
              {event.failure_message && (
                <p
                  className={styles.payment_failure_message}
                  data-testid="payment-failure-message"
                >
                  {event.failure_message}
                </p>
              )}
            </div>
          </div>
        )}
        {showInvoiceUrlRow && (
          <div className={styles.event_subtitle}>
            <span className={styles.icon_circle}>
              <LinkIcon />
            </span>
            <div className={styles.invoice_url_wrapper}>
              <a
                href={event.invoiceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.invoice_url_link}
              >
                {isCardOnFile ? t("View receipt") : t("View invoice")}
              </a>
              {showCopyPaymentLink && (
                <>
                  <span className={styles.invoice_url_separator}>•</span>
                  <a
                    onClick={handleCopyPaymentLink}
                    className={styles.invoice_url_link}
                    style={{ cursor: "pointer" }}
                  >
                    {t("Copy payment link")}
                  </a>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      <div className={styles.event_notes}>{event.notes}</div>
      {/* CN-2808: note for facility personnel (coaches, admins, schedulers). The API never sends it to athletes. */}
      {event.privateNotes && (
        <div className={styles.private_note} data-testid="event-private-note">
          <div className={styles.private_note_header}>
            <img src={LockIcon} alt="" className={styles.private_note_icon} />
            <span>{t("Private note")}</span>
            <span className={styles.private_note_badge}>{t("Facility personnel only")}</span>
          </div>
          <p className={styles.private_note_text}>{event.privateNotes}</p>
        </div>
      )}
      {scheduleContact && (
        <ScheduleModal
          isModalOpen={!!scheduleContact}
          modalHandler={() => setScheduleContact(null)}
          athleteData={scheduleContact}
          tabType="bookings"
          isUSGAIntegrationActive={isUSGAIntegrationActive}
        />
      )}
    </>
  );
};

export default ScheduleBlocksInfo;
